import Ajv from 'ajv';
import { actionIsSentOnCreate } from 'components/common/configPanel/actionSetup';
import addFormats from 'ajv-formats';
import addKeywords from 'ajv-keywords';
import { boundingBoxErrors } from './boundingBox';
import { polygonErrors } from './polygon';
import { dropBlankListItems, hasScalarListItems } from './blankListItems';

// Shared AJV pass over an integration's action configurations, used by the
// "New Connection" modal and the new-Route wizard — the two screens that commit
// every section in one submit.
//
// It validates EVERY section the screen shows, not just the ones the user
// touched: a required auth field the user never opened has no configuration
// entry at all, and iterating the entries let those screens create records with
// empty credentials.
export type ActionConfigErrors = Record<string, Record<string, string>>;

type Translate = (key: any, options?: any) => string;

// These messages are rendered under the field, so they are the user's copy, not
// AJV's. AJV's own text is English-only and names JSON-schema keywords, which is
// no help to anyone filling in a form — so each keyword maps to one translated
// sentence, with a general fallback for the rest.
const messageFor = (keyword: string, t: Translate): string => {
  switch (keyword) {
    case 'required':
      return t('This field is required.');
    case 'pattern':
    case 'format':
      return t("This value doesn't have the expected format.");
    case 'minLength':
    case 'minItems':
    case 'minimum':
    case 'exclusiveMinimum':
      return t('This value is too small or too short.');
    case 'maxLength':
    case 'maxItems':
    case 'maximum':
    case 'exclusiveMaximum':
      return t('This value is too large or too long.');
    case 'enum':
      return t('Choose one of the available options.');
    case 'type':
    default:
      return t('This value is not valid.');
  }
};

const buildAjv = () => {
  const ajv = new Ajv({ allErrors: true, verbose: true, strictTypes: false });
  // Portal-only schema keywords: they carry UI meaning, not validation.
  ajv.addKeyword({
    keyword: 'is_executable',
    validate: () => true,
    errors: false,
  });
  ajv.addKeyword({
    keyword: 'example',
    type: 'string',
    schemaType: ['string', 'object', 'number', 'array'],
    validate: () => true,
  });
  addFormats(ajv);
  // File widgets submit base64 payloads, which ajv-formats does not know.
  ajv.addFormat('data-url', {
    type: 'string',
    validate: (data: string) =>
      /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(
        data,
      ),
  });
  addKeywords(ajv);
  return ajv;
};

// A schema with if/then/else validates against the branch the data selects, so
// the required list depends on what the user picked.
//
// The branch is chosen from the data AS ENTERED, before the null-filling below.
// The two validators this replaced filled missing properties with null first,
// which made an `if: { required: [...] }` match on a field the user never
// entered — the branch was picked as though the field had been filled. Choosing
// from the raw data is the honest reading.
const resolveSchema = (ajv: Ajv, schema: any, data: any) => {
  // A conditional may carry only one arm — `if`/`then` with no `else` is
  // valid JSON Schema and common. Requiring both left the branch's fields out
  // of `required`, so AJV's error for them was discarded as "optional and
  // empty". Same rule as flowMap/validateActionData.
  if (!(schema.if && (schema.then || schema.else))) return schema;
  const branch = ajv.compile(schema.if)(data) ? schema.then : schema.else;
  if (!branch || typeof branch !== 'object') return schema;
  return {
    ...schema,
    properties: { ...schema.properties, ...branch.properties },
    // The branch ADDS requirements; it does not replace the base schema's.
    // Taking only the branch list let a base-required field (a tenant, say)
    // through whenever the schema had an if/then/else at all.
    required: Array.from(
      new Set([...(schema.required ?? []), ...(branch.required ?? [])]),
    ),
  };
};

// rjsf leaves untouched fields out of the form data entirely; AJV only reports a
// required field as missing, so fill the gaps with null to get one error per
// empty field.
const withNulls = (schema: any, data: any) => {
  const filled = { ...data };
  Object.keys(schema.properties ?? {}).forEach((key) => {
    if (!filled.hasOwnProperty(key) || filled[key] === undefined)
      filled[key] = null;
  });
  return filled;
};

// AJV's `required` only asks whether the property EXISTS, so a credential the
// user typed and then cleared ("") satisfies it. "Filled in" is what the rest of
// the app means by required here (see actionSetup.missingRequiredFields), so
// blank values are reported the same way a missing one is.
const isBlank = (value: any) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

// A JSON-pointer instancePath ("/endpoints/0") as its segments, unescaped.
const pathSegments = (instancePath: string): string[] =>
  instancePath
    .split('/')
    .slice(1)
    .map((segment) => segment.replace(/~1/g, '/').replace(/~0/g, '~'));

// An error inside a list row or a nested object: the top-level mapping keys
// messages by top-level field, so it cannot place these.
export const isNestedError = (error: any): boolean =>
  pathSegments(error?.instancePath ?? '').length >=
  (error?.keyword === 'required' ? 1 : 2);

// Follows $refs, single-entry allOf and the anyOf Pydantic v2 uses for
// Optional[...] down to the node that carries type/properties/items.
const resolveNode = (root: any, node: any): any => {
  for (let depth = 0; depth < 10 && node && typeof node === 'object'; depth++) {
    const ref =
      typeof node.$ref === 'string' &&
      node.$ref.match(/^#\/(definitions|\$defs)\/(.+)$/);
    const nonNull =
      Array.isArray(node.anyOf) && !node.items && !node.properties
        ? node.anyOf.filter((member: any) => member?.type !== 'null')
        : [];
    if (ref) node = root?.[ref[1]]?.[ref[2]];
    else if (Array.isArray(node.allOf) && node.allOf.length === 1)
      node = node.allOf[0];
    else if (nonNull.length === 1) node = nonNull[0];
    else break;
  }
  return node;
};

// The fields a nested object requires as it stands: its own list plus the one
// its if/then/else adds for this value (a row that needs a URL only when it is
// enabled). A row's branch is not merged into its schema the way a top-level
// one is, so it is resolved here per value.
const conditionChecks = new WeakMap<object, (data: any) => boolean>();
const requiredFor = (node: any, value: any): string[] => {
  const base: string[] = Array.isArray(node?.required) ? node.required : [];
  if (!node?.if || typeof node.if !== 'object' || !(node.then || node.else))
    return base;
  let matches = conditionChecks.get(node.if);
  if (!matches) {
    try {
      const check = buildAjv().compile(node.if);
      matches = (data: any) => !!check(data);
    } catch (e) {
      matches = () => false;
    }
    conditionChecks.set(node.if, matches);
  }
  const branch = matches(value ?? {}) ? node.then : node.else;
  return Array.isArray(branch?.required)
    ? Array.from(new Set([...base, ...branch.required]))
    : base;
};

// The schema node a path of data keys lands on; a list index steps into `items`.
const schemaAt = (root: any, segments: string[]): any =>
  resolveNode(
    root,
    segments.reduce((node: any, segment) => {
      const current = resolveNode(root, node);
      if (!current || typeof current !== 'object') return undefined;
      return current.type === 'array' || current.items
        ? current.items
        : current.properties?.[segment];
    }, root),
  );

// Top-level fields that render through generateUISchema's own components
// (CustomArrayField for a list of plain values, CustomObjectTemplate for an
// inline object). Those don't read rjsf's per-field errors, so their nested
// messages are shown on the top-level field instead.
const showsNestedErrors = (property: any): boolean => {
  if (!property || typeof property !== 'object') return true;
  if (property.type === 'object') return false;
  if (property.type === 'array') {
    const items = property.items;
    const types = Array.isArray(items?.type) ? items.type : [items?.type];
    return !(
      types.some((type: any) =>
        ['string', 'number', 'integer', 'boolean'].includes(type),
      ) || Array.isArray(items?.enum)
    );
  }
  return true;
};

// Where a nested error is shown. A list of plain values is validated without
// its blank rows (dropBlankListItems), so an item's index in the error may not
// be its index on screen: the message goes on the list itself instead.
const nestedErrorKey = (schema: any, segments: string[]): string => {
  if (!showsNestedErrors(schema?.properties?.[segments[0]])) return segments[0];
  for (let end = 1; end < segments.length; end++) {
    const node = schemaAt(schema, segments.slice(0, end));
    if (
      node?.type === 'array' &&
      hasScalarListItems(resolveNode(schema, node.items))
    )
      return segments.slice(0, end).join('.');
  }
  return segments.join('.');
};

// Required fields left blank inside list rows and nested objects. JSON
// Schema's `required` only checks that a key is present, so a row holding
// { url: "" } passes AJV although the field is empty; the top level has
// blankRequiredFields for the same gap.
export const nestedBlankRequiredErrors = (
  schema: any,
  data: any,
  t: Translate = (key: any) => key,
): Record<string, string> => {
  const out: Record<string, string> = {};
  const walk = (node: any, value: any, segments: string[], depth: number) => {
    const current = resolveNode(schema, node);
    if (!current || typeof current !== 'object' || depth > 10) return;
    if (Array.isArray(value)) {
      if (hasScalarListItems(resolveNode(schema, current.items))) return;
      value.forEach((item, index) =>
        walk(current.items, item, [...segments, String(index)], depth + 1),
      );
      return;
    }
    if (!value || typeof value !== 'object' || !current.properties) return;
    if (segments.length > 0)
      requiredFor(current, value).forEach((field: string) => {
        if (!isBlank(value[field])) return;
        const key = nestedErrorKey(schema, [...segments, field]);
        if (!out[key]) out[key] = messageFor('required', t);
      });
    Object.keys(current.properties).forEach((field) =>
      walk(
        current.properties[field],
        value[field],
        [...segments, field],
        depth + 1,
      ),
    );
  };
  walk(schema, data, [], 0);
  return out;
};

// Messages for errors inside list rows and nested objects, keyed by their
// dotted path ("endpoints.0.url"). SchemaForm shows each one under its field.
export const nestedFieldErrors = (
  ajvErrors: any[] | null | undefined,
  data: any,
  t: Translate = (key: any) => key,
  schema?: any,
): Record<string, string> => {
  const out: Record<string, string> = {};
  const nested = (ajvErrors ?? []).filter(isNestedError);
  // "One of these alternatives" fails as a whole: every branch's errors would
  // tell the user to fill in fields when any one branch would do.
  const alternatives = nested
    .filter((error: any) => ['anyOf', 'oneOf'].includes(error.keyword))
    .map((error: any) => error.instancePath);
  nested.forEach((error: any) => {
    // "must match the then schema" restates the branch's own errors, which
    // are reported by field.
    if (error.keyword === 'if') return;
    const isAlternative = ['anyOf', 'oneOf'].includes(error.keyword);
    if (
      !isAlternative &&
      alternatives.some(
        (path: string) =>
          error.instancePath === path ||
          error.instancePath.startsWith(`${path}/`),
      )
    )
      return;
    let isRequired = error.keyword === 'required';
    const segments = pathSegments(error.instancePath);
    if (isRequired) segments.push(error.params.missingProperty);
    const value = segments.reduce((node: any, key) => node?.[key], data);
    if (!isRequired && isBlank(value)) {
      // A required field holding "" or null is as empty as a missing one.
      // An optional field left out (null, undefined) is not an error, but one
      // holding a value, even "", must still be valid: it is submitted.
      const parentPath = segments.slice(0, -1);
      const parentValue = parentPath.reduce(
        (node: any, key) => node?.[key],
        data,
      );
      const leaf = segments[segments.length - 1];
      if (requiredFor(schemaAt(schema, parentPath), parentValue).includes(leaf))
        isRequired = true;
      else if (value === null || value === undefined) return;
    }
    const key = nestedErrorKey(schema, segments);
    if (!out[key])
      out[key] = messageFor(isRequired ? 'required' : error.keyword, t);
  });
  return out;
};

const blankRequiredFields = (schema: any, data: any): string[] =>
  (Array.isArray(schema.required) ? schema.required : []).filter(
    (field: string) => isBlank(data?.[field]),
  );

// Some conditionals keep every field on screen and only change which ones are
// required — iNaturalist's "choose a project, or draw a bounding box".
// Flagging just the branch's fields ("bounding box is required") hides the other way
// out: the empty field the `if` looks at (projects) would satisfy the rule too,
// yet it is on no required list, so it got no message at all.
//
// When a blank branch-required field could be excused by filling a blank field
// the `if` looks at, every field in that group gets one message pointing to the
// field descriptions and Help Center. The portal does not try to spell out the
// rule itself: an `if` can test anything, so "fill in X or Y" would be wrong for
// conditions other than "X is empty", and a section's description is general
// copy, not an error. A condition field that already holds a value
// (EarthRanger's authentication type) is a choice the user made, not an
// alternative, so the plain required message stands there.
export const conditionalRequirementMessages = (
  ajv: Ajv,
  schema: any,
  data: any,
  t: Translate = (key: any) => key,
): Record<string, string> => {
  if (!schema?.if || !(schema.then || schema.else)) return {};
  let matched: boolean;
  try {
    matched = ajv.compile(schema.if)(data ?? {}) as boolean;
  } catch (e) {
    return {};
  }
  const branch = matched ? schema.then : schema.else;
  if (!branch || typeof branch !== 'object') return {};

  const baseRequired: string[] = Array.isArray(schema.required)
    ? schema.required
    : [];
  const missing = (
    Array.isArray(branch.required) ? branch.required : []
  ).filter(
    (field: string) => !baseRequired.includes(field) && isBlank(data?.[field]),
  );
  if (!missing.length) return {};

  const properties = { ...schema.properties, ...branch.properties };
  const conditionFields = Array.from(
    new Set<string>([
      ...Object.keys(schema.if.properties ?? {}),
      ...(Array.isArray(schema.if.required) ? schema.if.required : []),
    ]),
  ).filter(
    (field) =>
      field in properties && !missing.includes(field) && isBlank(data?.[field]),
  );
  if (!conditionFields.length) return {};

  const message = t(
    'Field required. Consult the Help Center or the field description for more information.',
  );
  return Object.fromEntries(
    [...conditionFields, ...missing].map((field) => [field, message]),
  );
};

// The file widget holds its value as an array; the backend wants the JSON
// string. Applied to a COPY: the configurations these screens pass in are React
// state, and rewriting a widget's value behind its back leaves it rendering a
// string it cannot parse if the submit then fails on another field. Callers run
// this over the payload they are about to send — see serializeConfigurations.
export const serializeFileFields = (schema: any, data: any) => {
  if (!('files' in (schema?.properties ?? {})) || !Array.isArray(data?.files))
    return data;
  return {
    ...data,
    files: data.files.length ? JSON.stringify(data.files) : '',
  };
};

// What a creation screen submits: every configuration with its file fields
// serialised and its lists' blank rows dropped, and nothing mutated in place.
export const serializeConfigurations = (
  actions: any[] | undefined | null,
  configurations: any[] | undefined | null,
) =>
  (configurations ?? []).map((config: any) => {
    const action = (actions ?? []).find((a: any) => a.id === config.action);
    if (!action?.schema) return config;
    const data = serializeFileFields(
      action.schema,
      dropBlankListItems(action.schema, config.data ?? {}),
    );
    return data === config.data ? config : { ...config, data };
  });

// What a creation screen posts. A switched-off section is left out entirely: the
// API rejects any configuration with empty required fields, and an absent one
// means the action is unconfigured, which is the same "off" (GUNDI-5697). The
// exception is a connector defaulting the action to ON — see actionIsSentOnCreate.
export const configurationsForCreate = (
  actions: any[] | undefined | null,
  configurations: any[] | undefined | null,
) =>
  serializeConfigurations(
    actions,
    (configurations ?? []).filter((config: any) => {
      const action = (actions ?? []).find((a: any) => a.id === config.action);
      if (!action) return true;
      return actionIsSentOnCreate(action, config?.data);
    }),
  );

export const validateActionConfigs = (
  actions: any[] | undefined | null,
  configurations: any[] | undefined | null,
  // The screens pass their translator so the field errors speak the user's
  // language. Absent (tests, non-UI callers) the English key is the message.
  t: Translate = (key: any) => key,
): ActionConfigErrors => {
  const errors: ActionConfigErrors = {};
  (actions ?? []).forEach((action: any) => {
    const schema = action?.schema;
    if (!schema?.properties || typeof schema.properties !== 'object') return;
    const config = (configurations ?? []).find(
      (c: any) => c.action === action.id,
    );

    const ajv = buildAjv();
    // Validated as it will be posted: blank list rows are dropped from the
    // payload (serializeConfigurations), so they must not fail validation.
    const cleaned = dropBlankListItems(schema, config?.data ?? {});
    const submitted = serializeFileFields(schema, cleaned);
    const resolved = resolveSchema(ajv, schema, { ...submitted });
    const data = withNulls(resolved, submitted);
    blankRequiredFields(resolved, cleaned).forEach((field) => {
      if (!errors[action.id]) errors[action.id] = {};
      errors[action.id][field] = messageFor('required', t);
    });
    // Replaces the plain required message on the group, and reaches the
    // condition field AJV never reports.
    Object.entries(
      conditionalRequirementMessages(ajv, schema, submitted, t),
    ).forEach(([field, message]) => {
      if (!errors[action.id]) errors[action.id] = {};
      errors[action.id][field] = message;
    });

    // A bbox field holding typed text that is not a valid box yet (see
    // boundingBox.ts), or a polygon field holding an invalid shape (polygon.ts).
    Object.entries({
      ...boundingBoxErrors(action.ui_schema, config?.data, t),
      ...polygonErrors(action.ui_schema, config?.data, t),
    }).forEach(([field, message]) => {
      if (!errors[action.id]) errors[action.id] = {};
      if (!errors[action.id][field]) errors[action.id][field] = message;
    });

    Object.entries(nestedBlankRequiredErrors(resolved, submitted, t)).forEach(
      ([path, message]) => {
        if (!errors[action.id]) errors[action.id] = {};
        if (!errors[action.id][path]) errors[action.id][path] = message;
      },
    );

    const validate = ajv.compile(resolved);
    if (validate(data)) return;

    Object.entries(
      nestedFieldErrors(validate.errors, data, t, resolved),
    ).forEach(([path, message]) => {
      if (!errors[action.id]) errors[action.id] = {};
      if (!errors[action.id][path]) errors[action.id][path] = message;
    });
    validate.errors?.forEach((error: any) => {
      if (isNestedError(error)) return;
      const field = error.instancePath.slice(1) || error.params.missingProperty;
      const isRequired =
        Array.isArray(resolved.required) && resolved.required.includes(field);
      // An optional field left empty is not an error.
      if (!isRequired && (data[field] === null || data[field] === undefined))
        return;
      // A blank required field is reported as required, whatever keyword
      // AJV happened to trip on (a null fails `type` before `required`).
      const pattern = resolved.properties?.[field]?.pattern;
      const keyword =
        pattern && !new RegExp(pattern).test(data[field])
          ? 'pattern'
          : error.keyword;
      if (!errors[action.id]) errors[action.id] = {};
      // Never overwrite a message already set for this field: the first one
      // is the most specific (the required check runs before AJV).
      if (errors[action.id][field]) return;
      errors[action.id][field] = messageFor(keyword, t);
    });
  });
  return errors;
};
